import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { AppState, AnalysisResult, RecordingMetadata, UserProfile, Biomechanics, OverlayType, HubTab, HelpTab, MatchType, SoundProfile, SmartExercise, TimeSignature, SeverityMatrix, ActiveMission, AccessibilityMode, YouTubeReference } from '../types';
import { t, I18nKey } from '../services/i18n';
import { useUIState } from './hooks/useUIState';
import { useAudioSettings } from './hooks/useAudioSettings';
import { useGamification } from './hooks/useGamification';
import { useAnalysisCore } from './hooks/useAnalysisCore';

export { SAFE_GAIN_MIN, SAFE_GAIN_DEFAULT } from './hooks/useAudioSettings';

interface GuitarMatchContextType {
  // UI State
  appState: AppState;
  setAppState: (state: AppState) => void;
  activeOverlay: OverlayType;
  setActiveOverlay: (o: OverlayType) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (v: boolean) => void;
  hubTab: HubTab;
  setHubTab: (t: HubTab) => void;
  helpTab: HelpTab;
  setHelpTab: (t: HelpTab) => void;
  showSubscriptionOffer: boolean;
  setShowSubscriptionOffer: (v: boolean) => void;
  showSupportHub: boolean;
  setShowSupportHub: (v: boolean) => void;
  showTutorial: boolean;
  setShowTutorial: (v: boolean) => void;
  activeMission: ActiveMission | null;
  setActiveMission: (m: ActiveMission | null) => void;
  isCoachTransition: boolean;
  coachTransitionLabel: string | null;
  accessibilityMode: AccessibilityMode;
  
  // Audio & Settings
  targetBpm: number;
  setTargetBpm: (bpm: number) => void;
  timeSignature: TimeSignature;
  setTimeSignature: (s: TimeSignature) => void;
  // Fix: Added missing youtubeMasterRef property to context interface
  youtubeMasterRef: YouTubeReference | null;
  // Fix: Added missing setYoutubeMasterRef property to context interface
  setYoutubeMasterRef: (ref: YouTubeReference | null) => void;
  manualGrid: string;
  setManualGrid: (grid: string) => void;
  lang: 'en' | 'fr';
  setLang: (l: 'en' | 'fr') => void;
  micGain: number;
  setMicGain: (v: number) => void;
  soundProfile: SoundProfile;
  setSoundProfile: (p: SoundProfile) => void;
  globalCalibrationOffset: number;
  setGlobalCalibrationOffset: (v: number) => void;
  hasDoneGlobalCalibration: boolean;
  setHasDoneGlobalCalibration: (v: boolean) => void;
  
  // Analysis & Core
  refFile: File | null;
  setRefFile: (f: File | null) => void;
  studentFile: File | null;
  setStudentFile: (f: File | null, metadata?: RecordingMetadata) => void;
  result: AnalysisResult | null;
  setResult: (res: AnalysisResult | null) => void;
  currentLevelId: number;
  setCurrentLevelId: (id: number) => void;
  severityMatrix: SeverityMatrix;
  updateSeverity: (m: SeverityMatrix) => void;
  resetSeverityToFactory: () => void;
  setCoachLevel: (id: number) => void;
  resetAll: () => Promise<void>;
  updateBiomechanics: (b: Biomechanics | null) => void;
  renormalize: (levelId: number) => void;
  audioQualityWarning: string | null;
  setAudioQualityWarning: (v: string | null) => void;
  
  // Gamification & Business
  userProfile: UserProfile;
  updateUserProfile: (p: Partial<UserProfile>) => void;
  analysisCount: number;
  incrementAnalysisCount: () => void;
  isPremium: boolean;
  setIsPremium: (v: boolean) => void;
  matchType: MatchType;
  setMatchType: (t: MatchType) => void;
  matchSourceName: string;
  setMatchSourceName: (n: string) => void;
  
  // Helpers
  t: (key: I18nKey, params?: Record<string, string | number>) => string;
  applyExercisePreset: (ex: SmartExercise) => void;
}

const GuitarMatchContext = createContext<GuitarMatchContextType | undefined>(undefined);

export const GuitarMatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ui = useUIState();
  const settings = useAudioSettings();
  const gamification = useGamification();
  const core = useAnalysisCore(
      ui.setCoachTransitionLabel,
      ui.setIsCoachTransition,
      settings.setAtomicOffset
  );

  const i18nFunc = (key: I18nKey, params?: Record<string, string | number>) => t(key, settings.lang, params);

  const applyExercisePreset = (ex: SmartExercise) => {
      settings.setManualGrid(ex.preset_grid || settings.manualGrid);
      if (ex.preset_bpm) settings.setTargetBpm(ex.preset_bpm);
      if (ex.preset_level) core.setCurrentLevelId(ex.preset_level);
      gamification.setMatchType('FILE');
      // Fix: Ensure activeMission is set so HUD appears in Recorder
      ui.setActiveMission(ex);
      core.setAppState(AppState.ANALYZING);
  };

  const contextValue = useMemo(() => ({
      // UI
      appState: core.appState,
      setAppState: core.setAppState,
      activeOverlay: ui.activeOverlay,
      setActiveOverlay: ui.setActiveOverlay,
      isMenuOpen: ui.isMenuOpen,
      setIsMenuOpen: ui.setIsMenuOpen,
      hubTab: ui.hubTab,
      setHubTab: ui.setHubTab,
      helpTab: ui.helpTab,
      setHelpTab: ui.setHelpTab,
      showSubscriptionOffer: ui.showSubscriptionOffer,
      setShowSubscriptionOffer: ui.setShowSubscriptionOffer,
      showSupportHub: ui.showSupportHub,
      setShowSupportHub: ui.setShowSupportHub,
      showTutorial: ui.showTutorial,
      setShowTutorial: ui.setShowTutorial,
      activeMission: ui.activeMission,
      setActiveMission: ui.setActiveMission,
      isCoachTransition: ui.isCoachTransition,
      coachTransitionLabel: ui.coachTransitionLabel,
      accessibilityMode: ui.accessibilityMode,

      // Settings
      targetBpm: settings.targetBpm,
      setTargetBpm: settings.setTargetBpm,
      timeSignature: settings.timeSignature,
      setTimeSignature: settings.setTimeSignature,
      // Fix: Include youtubeMasterRef from core in context value
      youtubeMasterRef: core.youtubeMasterRef,
      // Fix: Include setYoutubeMasterRef from core in context value
      setYoutubeMasterRef: core.setYoutubeMasterRef,
      manualGrid: settings.manualGrid,
      setManualGrid: settings.setManualGrid,
      lang: settings.lang,
      setLang: settings.setLang,
      micGain: settings.micGain,
      setMicGain: settings.setMicGain,
      soundProfile: settings.soundProfile,
      setSoundProfile: settings.setSoundProfile,
      globalCalibrationOffset: settings.globalCalibrationOffset,
      setGlobalCalibrationOffset: settings.setGlobalCalibrationOffset,
      hasDoneGlobalCalibration: settings.hasDoneGlobalCalibration,
      setHasDoneGlobalCalibration: settings.setHasDoneGlobalCalibration,

      // Analysis
      refFile: core.refFile,
      setRefFile: core.setRefFile,
      studentFile: core.studentFile,
      setStudentFile: core.setStudentFile,
      result: core.result,
      setResult: core.setResult,
      currentLevelId: core.currentLevelId,
      setCurrentLevelId: core.setCurrentLevelId,
      severityMatrix: core.severityMatrix,
      updateSeverity: core.updateSeverity,
      resetSeverityToFactory: core.resetSeverityToFactory,
      setCoachLevel: core.setCoachLevel,
      resetAll: core.resetAll,
      updateBiomechanics: core.updateBiomechanics,
      renormalize: core.renormalize,
      audioQualityWarning: core.audioQualityWarning,
      setAudioQualityWarning: core.setAudioQualityWarning,

      // Gamification
      userProfile: gamification.userProfile,
      updateUserProfile: gamification.updateUserProfile,
      analysisCount: gamification.analysisCount,
      incrementAnalysisCount: gamification.incrementAnalysisCount,
      isPremium: gamification.isPremium,
      setIsPremium: gamification.setIsPremium,
      matchType: gamification.matchType,
      setMatchType: gamification.setMatchType,
      matchSourceName: gamification.matchSourceName,
      setMatchSourceName: gamification.setMatchSourceName,

      // Helpers
      t: i18nFunc,
      applyExercisePreset
  }), [ui, settings, gamification, core]);

  return <GuitarMatchContext.Provider value={contextValue}>{children}</GuitarMatchContext.Provider>;
};

export const useGuitarMatch = () => {
  const context = useContext(GuitarMatchContext);
  if (!context) throw new Error("useGuitarMatch must be used within a GuitarMatchProvider");
  return context;
};